import React, { useEffect, useState } from 'react'
import { getCategoryProducts } from '../../services/api/categories'
import { Address, Pack, Product, ProductOption } from '../../model'
import IconWithBackground from '../IconWithBackground/IconWithBackground'
import { ExtendedProduct } from '../../services/api/orders'
import { calculateFrontendTotalOptionsPrice, formatCurrencyValue, getBackendData } from '../../helpers/utils'
import PopularPackages from './PopularPackages'
import CategoryHeader from '../CategoryHeader'
import DefaultLoader from '../Loaders/DefaultLoader'
import ProductItem from '../ProductItem/ProductItem'
import Button from '../Button/Button'
import ProductOptionsModalContent from '../ProductOptionsModalContent/ProductOptionsModalContent'
import { useModals } from '../../hooks/useModals'
import ProductQuoteItem from '../ProductQuoteItem/ProductQuoteItem'
import EditButton from '../EditButton/EditButton'
import DynamicFaIcon from '../DynamicFaIcon/DynamicFaIcon'

interface ProductSelectionProps {
    selectedProducts: Product[]
    address: Address
    categoryId: number
    onSelectionChange(products: Product[]): void
    onNext(): void
}
const ProductSelection: React.FunctionComponent<ProductSelectionProps> = ({
    selectedProducts,
    address,
    categoryId,
    onSelectionChange,
    onNext
}: ProductSelectionProps) => {
    const [basket, setBasket] = useState<Product[]>(selectedProducts ? selectedProducts : [])
    const [productList, setProductList] = useState<Product[]>([])
    const [packageList, setPackageList] = useState<Pack[]>([])
    const [productWithQuoteList, setProductWithQuoteList] = useState<Product[]>([])
    const [loading, setLoading] = useState<boolean>(true) // set default on true
    const [totalPriceBasket, setTotalPriceBasket] = useState<string | number>(0)
    const [extraOptions, setExtraOptions] = useState<ProductOption[]>([])
    const [redirectAfterUpdate, setRedirectAfterUpdate] = useState<boolean>(false)

    const { showModal, RenderModals } = useModals([
        {
            id: 'productOptions',
            comp: ProductOptionsModalContent,
            props: {
                onConfirm: (product: ExtendedProduct, selectedOptions: ProductOption[]) => {
                    let productToAdd = null

                    if (product._isPack) {
                        productToAdd = {
                            ...product,
                            // @ts-ignore
                            products: product.products?.map((p) => ({
                                ...p,
                                availableOptions: p.availableOptions?.map((o: ProductOption) => ({
                                    ...o,
                                    _isAdded: selectedOptions.includes(o)
                                }))
                            }))
                        }
                    } else {
                        productToAdd = {
                            ...product,
                            availableOptions: product.availableOptions?.map((o: ProductOption) => ({
                                ...o,
                                _isAdded: selectedOptions.includes(o)
                            }))
                        }
                    }

                    updateBasket(productToAdd, productToAdd._isPack)
                }
            }
        }
    ])

    const fetchProductList = async (categoryId: number) => {
        setLoading(true)
        try {
            const data = await getCategoryProducts({ categoryUUID: categoryId })
            const productWithQuotes = data.products.filter((p: Product) => p.priceType === 'quote')
            const restOfProducts = data.products.filter((p: Product) => p.priceType !== 'quote')
            setProductList(restOfProducts)
            setProductWithQuoteList(productWithQuotes)
            setPackageList(
                data.packs.map((p: Pack) => {
                    return { ...p, _isPack: true }
                })
            )
        } catch (e) {
            console.error(e)
        }
        setLoading(false)
    }

    const calculateOrderTotal = async (currentBasket: Product[]) => {
        // Callback to parent with product selection
        onSelectionChange(currentBasket)
        try {
            // get all product options that are added
            // const productOptions = getMappedProductOptions(currentBasket)
            const data = {
                category: Number(categoryId),
                address: {
                    zipcode: address.zipcode,
                    extension: address.extension,
                    houseNumber: Number(address.houseNumber)
                },
                products: currentBasket
            }

            const results = await getBackendData(data)
            setTotalPriceBasket(results[0]?.totalPrice.toFixed(2) || '0,00')
            setExtraOptions(results[0]?.options)
        } catch (e) {
            console.error(e)
        } finally {
            if (redirectAfterUpdate) {
                onNext()
            }
        }
    }

    const checkQuoteProductsInBasket = (currentBasket: Product[]) => {
        const quoteProducts = currentBasket.filter((p: Product) => p.priceType === 'quote')
        if (quoteProducts.length > 0) {
            setBasket((prevState: Product[]) => {
                const newBasket = prevState.filter((product) => !quoteProducts.includes(product))
                return newBasket
            })
        }
    }

    useEffect(() => {
        fetchProductList(categoryId)
    }, [categoryId])

    /**
     * When basket is changed, re-calculate total
     */
    useEffect(() => {
        calculateOrderTotal(basket)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [basket])

    // Only remove quote items on mount
    useEffect(() => {
        checkQuoteProductsInBasket(basket)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const updateBasket = (addedProduct: any, isPack = false) => {
        const inBasket = basket.some((a) => a.id === addedProduct.id)
        if (inBasket) {
            setBasket((prevState: React.ComponentState) => {
                const newBasket = [...prevState]
                const idx = newBasket.findIndex((a) => a.id === addedProduct.id)
                idx >= 0 && newBasket.splice(idx, 1, addedProduct)
                return newBasket
            })
        } else {
            setBasket((prevState: React.ComponentState) => {
                let newBasket = [...prevState]

                if (isPack) {
                    // Remove pack if there is already a pack in the basket (only one allowed)
                    newBasket = newBasket.filter((p) => !p._isPack)
                    // Filter out existing products in the basket that are also in the pack
                    // Existing separate products are removed from the basket in favour of the pack
                    newBasket = newBasket.filter(
                        (p) => !p._isPack && !addedProduct.products.map((a: Product) => a.id).includes(p.id)
                    )
                }
                newBasket.push({ ...addedProduct, _isPack: isPack })
                return newBasket
            })
        }
    }

    const addQuoteProductToBasketAndNext = (addedProduct: ExtendedProduct, isPack = false) => {
        setRedirectAfterUpdate(true)
        setLoading(true)
        updateBasket(addedProduct, isPack)
    }

    const addProductToBasket = (addedProduct: any, isPack = false) => {
        const hasOptions =
            addedProduct.availableOptions?.length > 0 ||
            (isPack && addedProduct.products.filter((p: any) => p.availableOptions?.length > 0).length > 0)

        if (hasOptions) {
            showModal('productOptions', { product: addedProduct, isPack: isPack })
        } else {
            updateBasket(addedProduct, isPack)
        }
    }

    const removeProductFromBasket = (targetProduct: Product, isPack = false) => {
        setBasket((prevState: React.ComponentState) => {
            // return prevState.filter((curr: any) => curr.id !== targetProduct.id && curr._isPack !== targetProduct._isPack);
            const temp = [...prevState]
            const idx = temp.findIndex((a) => a.id === targetProduct.id && a._isPack === isPack)
            idx >= 0 && temp.splice(idx, 1)
            return temp
        })
    }

    const alreadyAddedFromPack = (product: Product) => {
        const packProducts = basket
            .filter((p: ExtendedProduct) => p._isPack) // filter packs only
            .flatMap((p: Pack) => p.products?.map((p: Product) => p.id)) // flatten products

        return packProducts.includes(product.id)
    }

    /**
     * Opens the product options modal for the given product.
     * @param {object} product - The product to edit options for.
     */
    const editOptions = (product: ExtendedProduct) => {
        showModal('productOptions', { product, isPack: product._isPack })
    }

    return (
        <div className='w-full mt-4 gap-4 grid grid-cols-1 md:grid-cols-12'>
            <RenderModals />

            <div className='flex flex-col col-span-1 md:col-span-8'>
                {loading ? (
                    <DefaultLoader />
                ) : (
                    <>
                        {productWithQuoteList.length > 0 && (
                            <div className='mb-4'>
                                <CategoryHeader title='Vraag direct aan' />
                                <div className='rounded-xl flex-1 shadow-sm border-full bg-white mt-2'>
                                    {productWithQuoteList.map((product: Product) => {
                                        const isDisabled = alreadyAddedFromPack(product)
                                        const productInBasket = basket.find(
                                            (curr: any) => curr._isPack === false && curr.id === product.id
                                        )
                                        return (
                                            <ProductQuoteItem
                                                key={product.id}
                                                product={product}
                                                showOptions={false}
                                                disabled={isDisabled}
                                                productInBasket={productInBasket}
                                                addProductToBasket={addQuoteProductToBasketAndNext}
                                                removeProductFromBasket={removeProductFromBasket}
                                            />
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                        <PopularPackages
                            packages={packageList}
                            selectedProducts={selectedProducts}
                            onSelectionChange={onselectionchange}
                            addProductToBasket={addProductToBasket}
                            removeProductFromBasket={removeProductFromBasket}
                        />
                        <div className='mt-4'>
                            <CategoryHeader title='Losse producten' />
                            <div className='rounded-xl flex-1 border-full shadow-sm bg-white mt-2'>
                                {productList.map((product: Product) => {
                                    const productInBasket = basket.find(
                                        (curr: any) => curr._isPack === false && curr.id === product.id
                                    )

                                    const isDisabled = alreadyAddedFromPack(product)

                                    return (
                                        <ProductItem
                                            key={`product-${product.id}`}
                                            product={product}
                                            showOptions={false}
                                            disabled={isDisabled}
                                            productInBasket={productInBasket}
                                            addProductToBasket={addProductToBasket}
                                            removeProductFromBasket={removeProductFromBasket}
                                        />
                                    )
                                })}
                            </div>
                        </div>
                    </>
                )}
            </div>

            <div className='w-full lg:mt-[2.2rem] col-span-1 md:col-span-4 flex justify-center md:justify-end'>
                <div className='w-full rounded-xl flex flex-col border-full shadow-sm bg-white self-start top-4 sticky'>
                    <div className='px-3 pt-3 pb-2 flex-shrink-0 flex-grow-0'>
                        <h2 className='text-base'>Samenvatting opdracht</h2>

                        {address && (
                            <div className='flex items-start'>
                                <IconWithBackground iconName='house' size='small' />
                                <div className='flex flex-col ml-2 text-xs'>
                                    <span>
                                        {address.street} {address.houseNumber}
                                        {address.extension ?? ''}
                                    </span>
                                    <span>
                                        {address.city}, {address.zipcode}
                                    </span>
                                    {address.surface && (
                                        <span>
                                            Woonoppervlakte: {address.surface} m<sup>2</sup>
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className='overflow-y-auto w-full flex-grow'>
                        {basket.map((product: any) => {
                            const hasOptions =
                                product.availableOptions?.length > 0 ||
                                product.products?.filter((p: any) => p.availableOptions?.length > 0).length > 0
                            const numOptions =
                                product.availableOptions?.filter((o: any) => o._isAdded).length ||
                                product.products
                                    ?.map((p: any) => p.availableOptions?.filter((o: any) => o._isAdded).length)
                                    .reduce((a: any, b: any) => a + b, 0)

                            let totalPrice = null

                            if (product._isPack) {
                                const totalOptionsPrice = product.products
                                    .map((p: any) => calculateFrontendTotalOptionsPrice([p]))
                                    .reduce((a: any, b: any) => a + b, 0)
                                totalPrice = formatCurrencyValue(product.price + totalOptionsPrice)
                            } else {
                                totalPrice = formatCurrencyValue(
                                    product.price + calculateFrontendTotalOptionsPrice([product])
                                )
                            }

                            return (
                                <div
                                    className='flex items-center gap-x-2 py-2 px-3 border-top'
                                    key={`${product.id}-${product.name}`}
                                >
                                    <IconWithBackground iconName='paint-brush' size='small' />
                                    <div className='flex-grow'>
                                        <h4 className='font-semibold text-xs'>
                                            {product.title || product.name}

                                            {numOptions > 0 && (
                                                <span className='textLightColor text-opacity-80'>
                                                    {' '}
                                                    (+{numOptions} optie{numOptions > 1 ? 's' : ''})
                                                </span>
                                            )}
                                        </h4>
                                        <span className='text-xs'>{totalPrice}</span>
                                    </div>

                                    {hasOptions && <EditButton onClick={() => editOptions(product)} />}
                                </div>
                            )
                        })}
                    </div>
                    <div className='border-top px-3 text-sm py-3 flex-shrink-0 flex flex-col'>
                        {extraOptions &&
                            extraOptions.length > 0 &&
                            extraOptions.map((a: any, index: number) => (
                                <span key={index} className='block mb-3 italic text-xs'>
                                    {a.name}: {formatCurrencyValue(a.price)}
                                </span>
                            ))}
                        <span className='font-semibold mb-3 block'>
                            Totaal: {totalPriceBasket ? totalPriceBasket : '0,00'}
                            <span className='text-xs font-normal text-gray-500 ml-1'>ex BTW</span>
                        </span>
                        <Button onClick={onNext}>
                            Volgende stap <DynamicFaIcon iconName='arrow-right' className='ml-2' />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ProductSelection
